import { ContentState } from "draft-js";
import { Api } from "@core/api/api";
import { filterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { contentStateToString } from "@utils/richText/converters";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { GroupDto, groupFromDto } from "@domain/group/dtos";
import { Group } from "@domain/group/group";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { MediaDto, mediaFromDto } from "@domain/media/dtos";
import { Media } from "@domain/media/media";
import { PostDto, postFromDto } from "@domain/post/dtos";
import { Post } from "@domain/post/post";
import { ProfileDto, profileFromDto } from "@domain/user/dtos";
import { Profile } from "@domain/user/profile";
import { DetailedPerson } from "@domain/user/user";
import { Accreditation } from "./accreditation";
import {
	accreditationDraftToDto,
	AccreditationDto,
	accreditationFromDto,
	organizationDraftToDto,
	OrganizationDto,
	organizationFromDto,
} from "./dtos";
import { Organization, OrganizationMemberRole } from "./organization";
import { OrganizationDraft } from "./organizationDraft";
import { OrganizationSubType, OrganizationType } from "./organizationType";
import { trackDraftToDto, TrackDto, trackFromDto } from "@domain/organization/track/dtos";
import { Track, TrackDraft } from "@domain/organization/track/track";

const ORGANIZATION_URL = "/organizations";

export class OrganizationApi {
	constructor(private readonly api: Api) {}

	async get(id: string): Promise<Organization> {
		const result = await this.api.get<OrganizationDto>(`${ORGANIZATION_URL}/${id}`);
		return organizationFromDto(result.data);
	}
	async getBySlug(slug: string): Promise<Organization> {
		const result = await this.api.get<OrganizationDto>(`${ORGANIZATION_URL}?slug=${slug}`);
		return organizationFromDto(result.data);
	}

	async getMyOrganizations(pageOptions: PaginationOptions): Promise<Page<DetailedPerson>> {
		const result = await this.api.get<Page<OrganizationDto>>(`/profile/organizations?${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((org) => organizationFromDto(org)) };
	}

	async getOrganizationShareroom(organizationId: string, pageOptions: PaginationOptions): Promise<Page<Group>> {
		const result = await this.api.get<Page<GroupDto>>(
			`${ORGANIZATION_URL}/groups/${organizationId}?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((group) => groupFromDto(group)) };
	}

	async create(draft: OrganizationDraft): Promise<Organization> {
		const result = await this.api.post<OrganizationDto>(ORGANIZATION_URL, organizationDraftToDto(draft));
		return organizationFromDto(result.data);
	}

	async getSuggestions(pageOptions: PaginationOptions): Promise<Page<Organization>> {
		const result = await this.api.get<Page<OrganizationDto>>(
			`${ORGANIZATION_URL}/suggested?${pageParams(pageOptions)}`
		);

		return { ...result.data, content: result.data.content.map((org) => organizationFromDto(org)) };
	}

	async getPosts(id: string, pageOptions: PaginationOptions): Promise<Page<Post>> {
		const result = await this.api.get<Page<PostDto>>(`${ORGANIZATION_URL}/${id}/posts?${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((post) => postFromDto(post)) };
	}

	async save(draft: OrganizationDraft, id: string): Promise<Organization> {
		const result = await this.api.put<OrganizationDto>(`${ORGANIZATION_URL}/${id}`, organizationDraftToDto(draft));
		return organizationFromDto(result.data);
	}

	async delete(id: string): Promise<void> {
		return this.api.delete(`${ORGANIZATION_URL}/${id}`);
	}

	async search(
		pageOptions: PaginationOptions,
		query: string,
		categories?: Category[],
		levels?: ContentLevel[],
		languages?: ContentLanguage[],
		durations?: CourseDuration[],
		providers?: CourseProvider[],
		orgTypes?: OrganizationType[],
		orgSubTypes?: OrganizationSubType[],
		countries?: CountryCode[]
	): Promise<Page<Organization>> {
		const result = await this.api.get<Page<OrganizationDto>>(
			`${ORGANIZATION_URL}?query=${query}&${pageParams(pageOptions)}&${filterParams(
				categories,
				levels,
				languages,
				durations,
				providers,
				orgTypes,
				orgSubTypes,
				countries
			)}`
		);
		return { ...result.data, content: result.data.content.map((org) => organizationFromDto(org)) };
	}

	async share(organizationId: string, message?: ContentState, groupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(`${ORGANIZATION_URL}/${organizationId}/share`, {
			text: message ? contentStateToString(message) : undefined,
			groupId,
		});
		return postFromDto(result.data);
	}

	async getAccreditations(id: string): Promise<Page<Accreditation>> {
		const result = await this.api.get<AccreditationDto[]>(`${ORGANIZATION_URL}/${id}/accreditations`);
		return {
			page: 0,
			total_pages: 1,
			total_size: result.data.length,
			content_size: result.data.length,
			page_size: result.data.length,
			content: result.data.map(accreditationFromDto),
		};
	}

	async createAccreditation(id: string, draft: Accreditation): Promise<Accreditation> {
		const result = await this.api.post<AccreditationDto>(
			`${ORGANIZATION_URL}/${id}/accreditations`,
			accreditationDraftToDto(draft)
		);
		return accreditationFromDto(result.data);
	}

	async createTrack(id: string, draft: TrackDraft): Promise<Track> {
		const result = await this.api.post<TrackDto>(`${ORGANIZATION_URL}/${id}/tracks`, trackDraftToDto(draft));
		return trackFromDto(result.data);
	}

	async getTracks(id: string): Promise<Page<Track>> {
		const result = await this.api.get<TrackDto[]>(`${ORGANIZATION_URL}/${id}/tracks`);
		return {
			page: 0,
			total_pages: 1,
			total_size: result.data.length,
			content_size: result.data.length,
			page_size: result.data.length,
			content: result.data.map(trackFromDto),
		};
	}

	async saveAccreditation(draft: Accreditation): Promise<Accreditation> {
		const result = await this.api.put<AccreditationDto>(
			`${ORGANIZATION_URL}/accreditations/${draft.id}`,
			accreditationDraftToDto(draft)
		);
		return accreditationFromDto(result.data);
	}
	async orderAccreditation(accreditations: Accreditation[], organizationId: string): Promise<void> {
		await this.api.put<AccreditationDto>(
			`${ORGANIZATION_URL}/${organizationId}/accreditations/update-order`,
			accreditations.map((acc, index) => ({ accreditationId: acc.id, newIndex: index }))
		);
		// return accreditationFromDto(result.data);
	}

	deleteAccreditation(id: string): Promise<void> {
		return this.api.delete(`${ORGANIZATION_URL}/accreditations/${id}`);
	}

	async getFollowers(id: string, pageOptions: PaginationOptions): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`${ORGANIZATION_URL}/${id}/followers?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async getFollowees(id: string, pageOptions: PaginationOptions): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`${ORGANIZATION_URL}/${id}/followees?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async getMembers(
		id: string,
		pageOptions: PaginationOptions,
		roles: OrganizationMemberRole[]
	): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`${ORGANIZATION_URL}/${id}/members?${pageParams(pageOptions)}&roles=${roles.join(",")}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	addMember(id: string, userId: string, role: OrganizationMemberRole): Promise<void> {
		return this.api.post(`${ORGANIZATION_URL}/${id}/members`, { userId, role });
	}

	deleteMember(id: string, userId: string, role: OrganizationMemberRole): Promise<void> {
		return this.api.delete(`${ORGANIZATION_URL}/${id}/members`, { params: { userId, role } });
	}

	follow(id: string): Promise<void> {
		return this.api.post(`${ORGANIZATION_URL}/${id}/follow`);
	}

	unfollow(id: string): Promise<void> {
		return this.api.post(`${ORGANIZATION_URL}/${id}/unfollow`);
	}

	async getAssets(id: string, pageOptions: PaginationOptions): Promise<Page<Media>> {
		const result = await this.api.get<Page<MediaDto>>(`${ORGANIZATION_URL}/${id}/assets?${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((asset) => mediaFromDto(asset)) };
	}

	async addAsset(id: string, assetIds: string[]): Promise<void> {
		return this.api.post(`${ORGANIZATION_URL}/${id}/assets/`, { assetIds });
	}

	deleteAsset(id: string, assetId: string): Promise<void> {
		return this.api.delete(`${ORGANIZATION_URL}/${id}/assets/${assetId}`);
	}
}
